"use client";

import * as React from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Link } from "@/i18n/navigation";
import { User } from "@/types/user";
import { signOut } from "next-auth/react";
import { useTranslations } from "next-intl";
import { NavItem } from "@/types/blocks/base";

interface SignUserProps {
  user: User;
  isAdmin?: boolean;
}

export default function SignUser({ user, isAdmin = false }: SignUserProps) {
  const t = useTranslations();

  const dropdownItems: NavItem[] = [
    {
      title: user.email,
    },
    {
      title: t("user.user_center"),
      url: "/my-orders",
    },
    ...(isAdmin
      ? [
          {
            title: t("user.admin_system"),
            url: "/admin/users",
          },
        ]
      : []),
    {
      title: t("user.sign_out"),
      onClick: () => signOut(),
    },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar className="size-9 cursor-pointer ring-1 ring-primary/25 transition-all duration-200 hover:ring-primary/45">
          <AvatarImage src={user.avatar_url} alt={user.nickname} />
          <AvatarFallback className="bg-primary/90 font-semibold text-primary-foreground">
            {user.nickname?.charAt(0)?.toUpperCase() || 'U'}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="mx-2 z-50 min-w-[320px] rounded-2xl border border-border/50 bg-background/95 p-2 text-foreground shadow-[0_24px_56px_rgba(0,0,0,0.35)] backdrop-blur-xl">
        {dropdownItems.map((item, index) => (
          <React.Fragment key={index}>
            <DropdownMenuItem
              className="mx-1 cursor-pointer justify-center rounded-xl px-4 py-3 text-foreground transition-colors hover:bg-muted focus:bg-muted focus:text-foreground"
            >
              {item.url ? (
                <Link href={item.url as any} target={item.target} className="w-full text-center">
                  {item.title}
                </Link>
              ) : index === 0 ? (
                <span className="w-full text-center text-muted-foreground">
                  {item.title}
                </span>
              ) : (
                <button onClick={item.onClick} className="w-full text-center">{item.title}</button>
              )}
            </DropdownMenuItem>
            {index !== dropdownItems.length - 1 && (
              <DropdownMenuSeparator className="mx-2 bg-border/50" />
            )}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
